import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";

const SITE_URL = "https://conceptions.ma";
const DEFAULT_OG = `${SITE_URL}/og-default.jpg`;

interface SeoProps {
  title: string;
  description: string;
  /** Path relatif (ex: "/services/industripub"). Sert pour canonical + hreflang. */
  path?: string;
  /** URL absolue de l'image OG (sinon défaut). */
  ogImage?: string;
  /** Type Open Graph. */
  type?: "website" | "article" | "product";
  /** JSON-LD additionnels (objets, sérialisés automatiquement). */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  /** noindex (ex: NotFound, dev pages) */
  noindex?: boolean;
  /** Mots-clés (meta keywords + AEO/LLM hints). */
  keywords?: string[];
}

/**
 * Composant SEO unique : title, description, canonical, hreflang, OG, Twitter,
 * lang/dir <html>, et n'importe quel nombre de blocs JSON-LD.
 */
export const Seo = ({
  title,
  description,
  path = "",
  ogImage = DEFAULT_OG,
  type = "website",
  jsonLd,
  noindex,
  keywords,
}: SeoProps) => {
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith("ar") ? "ar" : "fr";
  const dir = lang === "ar" ? "rtl" : "ltr";
  const canonical = `${SITE_URL}${path || "/"}`;
  const ogLocale = lang === "ar" ? "ar_MA" : "fr_MA";
  const ogLocaleAlt = lang === "ar" ? "fr_MA" : "ar_MA";
  const blocks = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
  const kw = keywords && keywords.length ? keywords.join(", ") : null;

  return (
    <Helmet htmlAttributes={{ lang, dir }}>
      <title>{title}</title>
      <meta name="description" content={description} />
      {kw && <meta name="keywords" content={kw} />}
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      <link rel="canonical" href={canonical} />

      {/* Hreflang : la SPA sert FR + AR (darija = ar-MA) sur la même URL */}
      <link rel="alternate" hrefLang="fr" href={canonical} />
      <link rel="alternate" hrefLang="fr-MA" href={canonical} />
      <link rel="alternate" hrefLang="ar" href={canonical} />
      <link rel="alternate" hrefLang="ar-MA" href={canonical} />
      <link rel="alternate" hrefLang="x-default" href={canonical} />

      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content={type} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:locale:alternate" content={ogLocaleAlt} />
      <meta property="og:site_name" content="Conceptions.ma" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {blocks.map((block, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  );
};

export const SITE = {
  url: SITE_URL,
  name: "Conceptions.ma",
  defaultOg: DEFAULT_OG,
};
